import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MissingMonomerReason, NucleotideComponent} from 'molbio/monomers/MonomerConstants';
import {SelfOpeningMonomerDropdown} from 'molbio/monomers/MonomerDropdown';
import {AliasData, updateAlias} from 'molbio/monomers/MonomersDAO';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {isServerRequestError} from 'util/Net';
import {InputChangeEvent} from 'util/Types';

function nullifyIfEmpty(input: string): string | null {
  return input.trim().length === 0 ? null : input;
}

interface Props {
  onRequestClose: () => void;
  onRequestSubmit: (data: AliasData) => void;
  initialState: AliasData;
}

export default function AliasEditModal(props: Props): JSX.Element {
  const [token, setToken] = useState(props.initialState.token);
  const [tokenVariantStart, setTokenVariantStart] = useState(props.initialState.tokenVariantStart ?? '');
  const [tokenVariantEnd, setTokenVariantEnd] = useState(props.initialState.tokenVariantEnd ?? '');
  const [sugarMonomerId, setSugarMonomerId] = useState(props.initialState.sugarMonomerId);
  const [baseMonomerId, setBaseMonomerId] = useState(props.initialState.baseMonomerId);
  const [phosphateMonomerId, setPhosphateMonomerId] = useState(props.initialState.phosphateMonomerId);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    const data = {
      token,
      tokenVariantStart: nullifyIfEmpty(tokenVariantStart),
      tokenVariantEnd: nullifyIfEmpty(tokenVariantEnd),
      sugarMonomerId,
      baseMonomerId,
      phosphateMonomerId:
        phosphateMonomerId === MissingMonomerReason.NULL_MONOMER ? null : phosphateMonomerId,
    };
    try {
      const updated = await updateAlias(props.initialState.syntaxId, props.initialState.id, data);
      props.onRequestSubmit(updated);
    } catch (e) {
      if (isServerRequestError(e)) {
        setError(notifications.getFriendlyError(e));
      } else {
        throw e;
      }
    }
  }

  const naturalMonomerIds = Object.values(BenchlingConfig.MODBIO_NATURAL_MONOMER_IDS_BY_NATURAL_ANALOGUE);
  const allMonomersNatural =
    naturalMonomerIds.includes(sugarMonomerId) &&
    naturalMonomerIds.includes(baseMonomerId) &&
    phosphateMonomerId &&
    naturalMonomerIds.includes(phosphateMonomerId);

  return (
    <Modal
      title="Edit alias"
      onRequestClose={() => {
        props.onRequestClose();
      }}
      onRequestSubmit={() => {
        submit();
      }}
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: token.trim().length === 0,
        },
      ]}
    >
      <>
        <div className="u-small-spacing-bottom">
          {error && <InlineBanner text={error} bannerType={InlineBannerType.ERROR} />}
          {allMonomersNatural && (
            <InlineBanner
              text="Updating a token mapped to natural monomers will not automatically recompute any affected computed fields."
              supportingInfo="If recomputation is necessary, request the manual recompute script be run."
              bannerType={InlineBannerType.WARNING}
            />
          )}
        </div>
        <div className={css(styles.row)}>
          <TextField
            className="u-small-spacing-bottom"
            label="Token"
            value={token}
            isRequired={true}
            onChange={(e: InputChangeEvent) => {
              setToken(e.target.value);
            }}
          />
          <TextField
            className="u-small-spacing-bottom"
            label="5' Variant"
            value={tokenVariantStart}
            isRequired={false}
            onChange={(e: InputChangeEvent) => {
              setTokenVariantStart(e.target.value);
            }}
          />
          <TextField
            className="u-small-spacing-bottom"
            label="3' Variant"
            value={tokenVariantEnd}
            isRequired={false}
            onChange={(e: InputChangeEvent) => {
              setTokenVariantEnd(e.target.value);
            }}
          />
        </div>
        <div className={css(styles.row)}>
          <div className={css(styles.monomerCol)}>
            <FieldLabel label="Sugar Monomer" isRequired={true} />
            <SelfOpeningMonomerDropdown
              nucleotideComponent={NucleotideComponent.SUGAR}
              selectedMonomerId={sugarMonomerId}
              updateSelectedMonomerId={setSugarMonomerId}
              fullWidth={true}
            />
          </div>
          <div className={css(styles.monomerCol)}>
            <FieldLabel label="Base Monomer" isRequired={true} />
            <SelfOpeningMonomerDropdown
              nucleotideComponent={NucleotideComponent.BASE}
              selectedMonomerId={baseMonomerId}
              updateSelectedMonomerId={setBaseMonomerId}
              fullWidth={true}
            />
          </div>
          <div className={css(styles.monomerCol)}>
            <FieldLabel label="Phosphate Monomer" isRequired={true} />
            <SelfOpeningMonomerDropdown
              nucleotideComponent={NucleotideComponent.PHOSPHATE}
              selectedMonomerId={phosphateMonomerId ?? MissingMonomerReason.NULL_MONOMER}
              updateSelectedMonomerId={setPhosphateMonomerId}
              includeNullMonomer={true}
              fullWidth={true}
            />
          </div>
        </div>
      </>
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  monomerCol: {
    width: '160px',
  },
});
